/**
 * Options for a "which PPJK" picker, from GET /api/v1/master-ceisa.
 *
 * That list is CEISA logins, not companies: one PPJK usually has several rows
 * with the same NPWP, and sometimes slightly different names. A picker wants
 * each PPJK once, so rows collapse by NPWP and the newest row (highest id)
 * names it — the same row the backend resolves a picked NPWP to.
 */
export interface CeisaUserRow {
  id: number;
  npwp?: string | null;
  name?: string | null;
  isActive?: boolean;
}

export interface PpjkOption {
  /** The PPJK's NPWP: what the picker sends. */
  value: string;
  label: string;
}

export function ppjkOptionsFrom(rows: CeisaUserRow[], ownNpwp?: string | null): PpjkOption[] {
  const newest = new Map<string, CeisaUserRow>();
  for (const row of rows) {
    const npwp = (row.npwp ?? '').trim();
    if (!npwp) continue;
    const seen = newest.get(npwp);
    if (!seen || row.id > seen.id) newest.set(npwp, row);
  }

  const options: PpjkOption[] = Array.from(newest.entries()).map(([npwp, row]) => {
    const name = (row.name ?? '').trim();
    return { value: npwp, label: name ? `${name} (${npwp})` : npwp };
  });
  options.sort((a, b) => a.label.localeCompare(b.label));

  // The caller's own PPJK is always pickable, and first, even when the list
  // has not loaded or does not carry it: the picker's default must be one of
  // its own options.
  const own = (ownNpwp ?? '').trim();
  if (!own) return options;
  const index = options.findIndex((option) => option.value === own);
  const mine = index >= 0 ? options.splice(index, 1)[0] : { value: own, label: own };
  return [mine, ...options];
}
